import { apiGet, apiPost, apiPut, apiDelete } from './api';
import { UpgradeRequiredError } from '../types/premiumTypes';
import type {
  Group,
  GroupMember,
  GroupMessage,
  CreateGroupRequest,
  UpdateGroupRequest,
  GroupFilters,
  PaginatedGroupsResponse,
  GroupStats,
  PaginationParams,
  SortParams,
} from '../types';

// Transform API response to frontend format
const transformGroup = (apiGroup: any): Group => ({
  ...apiGroup,
  currentMembers: apiGroup._count?.members || 0,
  tags: apiGroup.tags || [],
  isPublic: apiGroup.isPublic !== false, // Default to true if not specified
  currentUserMemberStatus: apiGroup.currentUserMemberStatus || null,
  // Map currentUserMemberStatus to membershipStatus for backward compatibility
  membershipStatus: apiGroup.currentUserMemberStatus || 'NOT_MEMBER',
  event: apiGroup.event ? {
    ...apiGroup.event,
    name: apiGroup.event.title || apiGroup.event.name, // Map title to name for compatibility
  } : undefined,
});

class GroupService {
  /**
   * Get all groups with optional filters and pagination
   */
  async getGroups(
    filters?: GroupFilters,
    pagination?: PaginationParams,
    sort?: SortParams,
  ): Promise<PaginatedGroupsResponse> {
    const params = new URLSearchParams();
    
    if (filters?.eventId) params.append('eventId', filters.eventId.toString());
    if (filters?.isPublic !== undefined) params.append('isPublic', filters.isPublic.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.availableOnly) params.append('availableOnly', filters.availableOnly.toString());
    if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
    
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    
    if (sort?.sortBy) params.append('sortBy', sort.sortBy);
    if (sort?.sortOrder) params.append('sortOrder', sort.sortOrder);

    const queryString = params.toString();
    const url = queryString ? `/groups?${queryString}` : '/groups';
    
    const response = await apiGet<PaginatedGroupsResponse>(url);
    
    // Transform groups to frontend format
    return {
      ...response,
      groups: response.groups.map(transformGroup),
    };
  }

  /**
   * Get a specific group by ID
   */
  async getGroup(groupId: number): Promise<Group> {
    const group = await apiGet<any>(`/groups/${groupId}`);
    return transformGroup(group);
  }

  /**
   * Get a specific group by ID (alias for getGroup)
   */
  async getGroupById(groupId: number): Promise<Group> {
    return this.getGroup(groupId);
  }

  /**
   * Get groups that the current user is a member of
   */
  async getMyGroups(pagination?: PaginationParams): Promise<PaginatedGroupsResponse> {
    const params = new URLSearchParams();
    
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());

    const queryString = params.toString();
    const url = queryString ? `/groups/my-groups?${queryString}` : '/groups/my-groups';
    
    const response = await apiGet<PaginatedGroupsResponse>(url);
    
    // Transform groups to frontend format
    return {
      ...response,
      groups: response.groups.map(transformGroup),
    };
  }

  /**
   * Create a new group
   */
  async createGroup(groupData: CreateGroupRequest | FormData): Promise<Group> {
    try {
      let group;
      
      // Handle both FormData and regular objects
      if (groupData instanceof FormData) {
        // Let browser set multipart boundary for FormData
        group = await apiPost<any>('/groups', groupData);
      } else {
        group = await apiPost<any>('/groups', groupData);
      }
      
      return transformGroup(group);
    } catch (error: any) {
      
      // Check if it's a premium upgrade required error
      if (error.status === 402 && error.data?.requiresUpgrade) {
        throw new UpgradeRequiredError({
          message: error.data.message || error.message,
          requiresUpgrade: error.data.requiresUpgrade,
          upgradeType: error.data.upgradeType,
          price: error.data.price || 4.95,
        });
      }
      throw error;
    }
  }

  /**
   * Update an existing group
   */
  async updateGroup(groupId: number, groupData: UpdateGroupRequest | FormData): Promise<Group> {
    let group;
    
    // Handle both FormData and regular objects
    if (groupData instanceof FormData) {
      // Let browser set multipart boundary for FormData
      group = await apiPut<any>(`/groups/${groupId}`, groupData);
    } else {
      group = await apiPut<any>(`/groups/${groupId}`, groupData);
    }
    
    return transformGroup(group);
  }

  /**
   * Delete a group (owner only)
   */
  async deleteGroup(groupId: number): Promise<void> {
    return apiDelete<void>(`/groups/${groupId}`);
  }

  /**
   * Join a group
   */
  async joinGroup(groupId: number): Promise<GroupMember> {
    try {
      return await apiPost<GroupMember>(`/groups/${groupId}/join`);
    } catch (error: any) {
      // Check if it's a premium upgrade required error
      if (error.status === 402 && error.data?.requiresUpgrade) {
        throw new UpgradeRequiredError({
          message: error.data.message || error.message,
          requiresUpgrade: error.data.requiresUpgrade,
          upgradeType: error.data.upgradeType,
          price: error.data.price || 4.95,
        });
      }
      throw error;
    }
  }

  /**
   * Leave a group
   */
  async leaveGroup(groupId: number): Promise<void> {
    return apiDelete<void>(`/groups/${groupId}/leave`);
  }

  /**
   * Get group members
   */
  async getGroupMembers(
    groupId: number,
    pagination?: PaginationParams,
  ): Promise<{ members: GroupMember[]; pagination: any }> {
    const params = new URLSearchParams();
    
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());

    const queryString = params.toString();
    const url = queryString 
      ? `/groups/${groupId}/members?${queryString}` 
      : `/groups/${groupId}/members`;
    
    const members = await apiGet<GroupMember[]>(url);
    return { members, pagination: null };
  }

  /**
   * Get pending join requests for a group (owner only)
   */
  async getGroupRequests(groupId: number): Promise<GroupMember[]> {
    return apiGet<GroupMember[]>(`/groups/${groupId}/requests`);
  }

  /**
   * Accept a pending member (owner only)
   */
  async acceptMember(groupId: number, memberId: number): Promise<void> {
    try {
      return await apiPut<void>(`/groups/${groupId}/members/${memberId}/status`, { status: 'ACCEPTED' });
    } catch (error: any) {
      // Check if it's a premium upgrade required error
      if (error.status === 402 && error.data?.requiresUpgrade) {
        throw new UpgradeRequiredError({
          message: error.data.message || error.message,
          requiresUpgrade: error.data.requiresUpgrade,
          upgradeType: error.data.upgradeType,
          price: error.data.price || 4.95,
        });
      }
      throw error;
    }
  }

  /**
   * Reject a pending member (owner only)
   */
  async rejectMember(groupId: number, memberId: number): Promise<void> {
    return apiPut<void>(`/groups/${groupId}/members/${memberId}/status`, { status: 'REJECTED' });
  }

  /**
   * Remove a member from group (owner only)
   */
  async removeMember(groupId: number, memberId: number): Promise<void> {
    return apiDelete<void>(`/groups/${groupId}/members/${memberId}`);
  }

  /**
   * Transfer group ownership to another member (owner only)
   */
  async transferOwnership(groupId: number, newOwnerId: number): Promise<void> {
    return apiPut<void>(`/groups/${groupId}/transfer-ownership`, { userId: newOwnerId });
  }

  /**
   * Get group messages with cursor-based pagination
   */
  async getGroupMessages(
    groupId: number,
    options?: { cursor?: string; limit?: number; before?: string }
  ): Promise<{
    messages: GroupMessage[];
    pagination: {
      nextCursor: string | null;
      hasNextPage: boolean;
    };
    hasMore: boolean
  }> {
    const params = new URLSearchParams();
    
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.before) params.append('before', options.before);

    const queryString = params.toString();
    const url = queryString 
      ? `/groups/${groupId}/messages?${queryString}` 
      : `/groups/${groupId}/messages`;
    
    const response = await apiGet<{
      messages: any[];
      pagination: {
        nextCursor: string | null;
        hasNextPage: boolean;
      };
    }>(url);

    // Transform backend message format to frontend format  
    const messages: GroupMessage[] = response.messages.map((msg: any) => ({
      id: parseInt(msg.id),
      groupId: groupId,
      userId: parseInt(msg.user.id),
      content: msg.content,
      messageType: msg.messageType || 'TEXT',
      gifUrl: msg.gifUrl || undefined,
      gifId: msg.gifId || undefined,
      createdAt: msg.createdAt,
      user: {
        id: parseInt(msg.user.id),
        username: msg.user.username,
        profilePhoto: msg.user.profilePhoto,
        suspendedAt: msg.user.suspendedAt,
      },
    }));

    return {
      messages,
      pagination: response.pagination,
      hasMore: response.pagination.hasNextPage,
    };
  }

  /**
   * Send a message to a group
   */
  async sendMessage(groupId: number, content: string): Promise<GroupMessage> {
    return apiPost<GroupMessage>(`/groups/${groupId}/messages`, { content });
  }

  /**
   * Get group statistics for dashboard
   */
  async getGroupStats(): Promise<GroupStats> {
    return apiGet<GroupStats>('/groups/stats');
  }

  /**
   * Search groups across all events
   */
  async searchGroups(
    query: string,
    filters?: Omit<GroupFilters, 'search'>,
    pagination?: PaginationParams,
  ): Promise<PaginatedGroupsResponse> {
    const params = new URLSearchParams();
    params.append('search', query);
    
    if (filters?.eventId) params.append('eventId', filters.eventId.toString());
    if (filters?.isPublic !== undefined) params.append('isPublic', filters.isPublic.toString());
    if (filters?.availableOnly) params.append('availableOnly', filters.availableOnly.toString());
    if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
    
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());

    return apiGet<PaginatedGroupsResponse>(`/groups/search?${params.toString()}`);
  }

  /**
   * Get popular tags for group creation
   */
  async getPopularTags(eventId?: number): Promise<string[]> {
    const params = eventId ? `?eventId=${eventId}` : '';
    return apiGet<string[]>(`/groups/popular-tags${params}`);
  }
}

export const groupService = new GroupService();